/**
 * Recently Viewed — tracker
 * Include this once, site-wide, e.g. near the end of theme.liquid:
 *   <script src="{{ 'recently-viewed-tracker.js' | asset_url }}" defer="defer"></script>
 *
 * Records every product the visitor lands on (full page load) AND every
 * product they see via an in-place swatch swap (the 'product:swapped'
 * event dispatched by product-swap-links), with no limit on list length.
 * Most-recently-seen handle is always first, duplicates are removed.
 * Entries older than 24 hours are dropped automatically.
 */
(function () {
  const STORAGE_KEY = 'recently_viewed_handles';
  const EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours

  function getHandleFromUrl(url) {
    try {
      const path = new URL(url, window.location.origin).pathname;
      const match = path.match(/\/products\/([a-zA-Z0-9\-_%]+)/);
      return match ? decodeURIComponent(match[1]) : null;
    } catch (e) {
      return null;
    }
  }

  // Stored shape is now [{ handle, ts }, ...], most-recent first. ts is
  // the epoch ms at which that product was last viewed, so each entry
  // can be aged out independently 24 hours after it was actually seen.
  function readEntries() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const list = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(list)) return [];

      const now = Date.now();
      return list
        .map((entry) => {
          // Migrate the old format (a bare handle string, no timestamp)
          // by treating it as viewed "now" rather than dropping it —
          // avoids wiping everyone's list the moment this ships.
          if (typeof entry === 'string') return { handle: entry, ts: now };
          if (entry && typeof entry.handle === 'string' && typeof entry.ts === 'number') return entry;
          return null;
        })
        .filter((entry) => entry && now - entry.ts < EXPIRY_MS);
    } catch (e) {
      return [];
    }
  }

  function writeEntries(entries) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
    } catch (e) {
      /* storage full or unavailable — silently skip */
    }
  }

  function track(handle) {
    if (!handle) return;
    const entries = readEntries().filter((entry) => entry.handle !== handle);
    entries.unshift({ handle: handle, ts: Date.now() }); // most recent first, no limit
    writeEntries(entries);
  }

  function remove(handle) {
    if (!handle) return;
    writeEntries(readEntries().filter((entry) => entry.handle !== handle));
  }

  function getHandles() {
    // Prune expired entries from storage on every read too, not just in
    // memory, so the stored list doesn't quietly grow forever with dead
    // entries that never get written back until the next track() call.
    const entries = readEntries();
    writeEntries(entries);
    return entries.map((entry) => entry.handle);
  }

  // Track the current page on load, if it's a product page.
  track(getHandleFromUrl(window.location.href));

  // Track whenever product-swap-links swaps in a different product
  // (swatch click) without a full page navigation.
  window.addEventListener('product:swapped', function (event) {
    const url = event && event.detail && event.detail.url;
    if (url) track(getHandleFromUrl(url));
  });

  // Also track on the app-level swap's popstate/pushState navigations,
  // in case the URL changes without a 'product:swapped' event on some pages.
  window.addEventListener('popstate', function () {
    track(getHandleFromUrl(window.location.href));
  });

  // Expose a small API in case other code wants to read/clear the list.
  window.RecentlyViewed = {
    getHandles: getHandles,
    remove: remove,
    clear: function () {
      writeEntries([]);
    },
  };
})();